import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Baby, ShieldCheck, Sparkles, Lock, Unlock, Radio, Palette, Film, BedDouble, BookOpen, GraduationCap, Users, Tv as TvIcon, MessageCircle, Zap, Gamepad2, ShoppingBag, Calendar } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useKidMode } from "../context/KidModeContext";
import { toast } from "sonner";
import { JrPodcastStudio, KidsDraw, KidsBedtime, KidsShorts } from "../components/KidsActivities";

/**
 * Public Kids Network landing page.
 *
 * This is the web-friendly (phone / tablet / desktop) mirror of the
 * bigger /tv/kids TV app — giving everyone the same Fun Zone, activities,
 * kid creator directory, and feed no matter what device they're on.
 */
export default function Kids() {
  const { user } = useAuth();
  const km = useKidMode();
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const [exitPin, setExitPin] = useState("");

  // Activity overlays (same as TvKidsApp)
  const [jrStudioOpen, setJrStudioOpen] = useState(false);
  const [drawOpen, setDrawOpen] = useState(false);
  const [bedtimeOpen, setBedtimeOpen] = useState(false);
  const [shortsOpen, setShortsOpen] = useState(false);

  const [creators, setCreators] = useState([]);
  const [feed, setFeed] = useState([]);
  const [liveNow, setLiveNow] = useState([]);
  const [movies, setMovies] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/kids-public/creators?limit=40").then((r) => setCreators(r.data?.creators || [])).catch(() => {});
    api.get("/kids-public/feed?limit=30").then((r) => setFeed(r.data?.posts || [])).catch(() => {});
    api.get("/kids-public/live").then((r) => setLiveNow(r.data?.live || [])).catch(() => {});
    api.get("/kids-public/movies?limit=20").then((r) => setMovies(r.data?.movies || [])).catch(() => {});
    api.get("/kids-public/categories").then((r) => setCategories(r.data?.categories || [])).catch(() => {});
  }, []);

  const enterKidMode = () => {
    const res = km.enterKidMode(pin);
    if (!res.ok) { toast.error(res.error); return; }
    setPin(""); toast.success("Kid Mode ON");
  };
  const exit = () => {
    const res = km.exitKidMode(exitPin);
    if (!res.ok) { toast.error(res.error); return; }
    setExitPin(""); toast.success("Exited Kid Mode — welcome back to Tap IN!");
    // Leave the walled garden and land on the main site home so grown-ups
    // know the exit worked.
    navigate("/", { replace: true });
  };

  const ACTIVITIES = [
    { key: "jr",  label: "Jr Podcast",   sub: "Go live with friends",    icon: Radio,    bg: "from-pink-400 via-fuchsia-500 to-purple-600",  onClick: () => setJrStudioOpen(true), testid: "kids-public-jr-btn" },
    { key: "dr",  label: "Draw & Share", sub: "Art for grown-ups",       icon: Palette,  bg: "from-orange-400 via-pink-500 to-rose-500",      onClick: () => setDrawOpen(true),     testid: "kids-public-draw-btn" },
    { key: "bt",  label: "Bedtime",      sub: "Lullabies to sleep to",   icon: BedDouble, bg: "from-indigo-500 via-violet-600 to-slate-800",  onClick: () => setBedtimeOpen(true),  testid: "kids-public-bedtime-btn" },
    { key: "sh",  label: "Kid Shorts",   sub: "Tiny fun videos",         icon: Film,     bg: "from-cyan-400 via-sky-500 to-blue-700",         onClick: () => setShortsOpen(true),   testid: "kids-public-shorts-btn" },
    { key: "tu",  label: "Tutoring",     sub: "Learn 1-on-1",            icon: GraduationCap, bg: "from-emerald-400 via-teal-500 to-green-700", onClick: null,                        testid: "kids-public-tutor-btn", to: "/kids/tutoring" },
    { key: "tv",  label: "Kids TV",      sub: "Big screen version",      icon: TvIcon,   bg: "from-yellow-400 via-amber-500 to-orange-500",   onClick: null,                        testid: "kids-public-tv-btn",    to: "/tv/kids" },
    { key: "gm",  label: "Games",        sub: "Play safe mini-games",   icon: Gamepad2, bg: "from-lime-400 via-emerald-500 to-teal-600",       onClick: null,                        testid: "kids-public-games-btn",      to: "/kids/games" },
    { key: "sh2", label: "Shop",         sub: "Cool kid goodies",        icon: ShoppingBag, bg: "from-amber-400 via-pink-500 to-fuchsia-600", onClick: null,                        testid: "kids-public-shop-btn",       to: "/kids/shop" },
    { key: "rd",  label: "Radio",        sub: "Safe music & stories",    icon: Radio,    bg: "from-violet-500 via-fuchsia-500 to-rose-500",     onClick: null,                        testid: "kids-public-radio-btn",      to: "/kids/radio" },
    { key: "gd",  label: "TV Guide",     sub: "What's on today",         icon: Calendar, bg: "from-sky-400 via-indigo-500 to-purple-700",       onClick: null,                        testid: "kids-public-guide-btn",      to: "/kids/guide" },
    { key: "cm",  label: "Community",    sub: "Say hi & see posts",      icon: MessageCircle, bg: "from-fuchsia-500 via-pink-500 to-rose-400", onClick: null,                        testid: "kids-public-community-btn", to: "/kids/community" },
    { key: "rl",  label: "Reels",        sub: "Swipe for fun",           icon: Zap,      bg: "from-violet-500 via-purple-500 to-indigo-700",  onClick: null,                        testid: "kids-public-reels-btn",     to: "/kids/reels" },
  ];

  return (
    <div data-testid="kids-page" className="pb-20">
      {/* ═══════════ 1. HERO — Learn and Play banner ═══════════ */}
      <section data-testid="kids-hero" className="px-3 sm:px-6 md:px-10">
        <div className="relative max-w-6xl mx-auto rounded-2xl md:rounded-[2rem] overflow-hidden shadow-2xl ring-4 ring-white/20">
          <img
            src="/kids-learn-play-banner.png"
            alt="Learn and Play — Tap In Kids Network"
            className="w-full h-auto block"
            draggable={false}
          />
        </div>
        <div className="max-w-6xl mx-auto mt-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 bg-yellow-300 text-black font-black text-[10px] sm:text-xs uppercase tracking-[0.25em] px-3 py-1 rounded-full">
              <Sparkles className="w-3 h-3" /> Safe · Fun · Educational
            </div>
            <h1 className="font-display font-black text-2xl sm:text-4xl md:text-5xl text-white tracking-tighter mt-2">
              A world made for kids.
            </h1>
            <p className="text-white/70 text-sm mt-1">Games, stories, tutoring, podcasts &amp; more — on every device.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {user ? (
              <Link data-testid="kids-cta-become" to="/kids/become-creator" className="bg-yellow-300 hover:bg-yellow-200 text-black font-bold px-4 py-2 rounded-full text-sm inline-flex items-center gap-1">
                <Sparkles className="w-4 h-4" /> Become a Kid Creator
              </Link>
            ) : (
              <Link to="/signup" className="bg-yellow-300 hover:bg-yellow-200 text-black font-bold px-4 py-2 rounded-full text-sm">
                Sign up to join
              </Link>
            )}
            <Link to="/tv/kids" className="tv-glass text-white font-bold px-4 py-2 rounded-full text-sm border border-white/30 hover:bg-white/15 inline-flex items-center gap-1">
              <TvIcon className="w-4 h-4" /> TV version
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════════ 2. FUN ZONE — 6 activity tiles ═══════════ */}
      <section data-testid="kids-public-fun-zone" className="mt-8 sm:mt-10 px-3 sm:px-6 md:px-10 max-w-6xl mx-auto">
        <h2 className="font-display font-black text-xl sm:text-3xl text-white mb-4 inline-flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-pink-400" /> Fun Zone
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {ACTIVITIES.map((t) => {
            const Icon = t.icon;
            const body = (
              <>
                <div className="text-white/80 mb-1"><Icon className="w-6 h-6" /></div>
                <div className="font-display font-black text-lg leading-tight drop-shadow">{t.label}</div>
                <div className="text-white/85 text-xs font-bold">{t.sub}</div>
              </>
            );
            const cls = `relative overflow-hidden rounded-2xl aspect-[4/3] ring-4 ring-white/80 shadow-xl bg-gradient-to-br ${t.bg} text-white text-left p-3 sm:p-4 flex flex-col justify-end hover:scale-[1.04] transition`;
            return t.to
              ? <Link key={t.key} to={t.to} data-testid={t.testid} className={cls}>{body}</Link>
              : <button key={t.key} data-testid={t.testid} onClick={t.onClick} className={cls}>{body}</button>;
          })}
        </div>
      </section>

      {/* ═══════════ 2b. Kids LIVE now — red pulsing rail ═══════════ */}
      {liveNow.length > 0 && (
        <section data-testid="kids-public-live-rail" className="mt-8 px-3 sm:px-6 md:px-10 max-w-6xl mx-auto">
          <h2 className="font-display font-black text-xl sm:text-3xl text-white mb-3 inline-flex items-center gap-2">
            <span className="inline-flex items-center gap-1 bg-red-600 text-white font-black text-[10px] uppercase tracking-[0.25em] px-2 py-0.5 rounded-full animate-pulse">LIVE</span>
            On right now
          </h2>
          <div className="flex gap-3 overflow-x-auto pb-3 snap-x -mx-1 px-1" style={{ scrollbarWidth: "none" }}>
            {liveNow.map((s) => (
              <Link
                key={s.id}
                to={`/kids/live/${s.id}`}
                data-testid={`kids-live-tile-${s.id}`}
                className="snap-start shrink-0 w-56 rounded-2xl overflow-hidden ring-2 ring-red-500/60 hover:ring-4 transition bg-black"
              >
                <div className="aspect-video bg-black relative">
                  {s.thumbnail_url
                    ? <img src={s.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center text-4xl">🎙️</div>}
                  <div className="absolute top-2 left-2 inline-flex items-center gap-1 bg-red-600 text-white font-black text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full animate-pulse">
                    <span className="w-1.5 h-1.5 rounded-full bg-white" /> LIVE
                  </div>
                  {s.viewer_count > 0 && (
                    <div className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">👀 {s.viewer_count}</div>
                  )}
                </div>
                <div className="p-2">
                  <div className="text-white text-sm font-bold truncate">{s.title}</div>
                  <div className="text-white/60 text-[10px] truncate">@{s.handle || s.creator_name}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ═══════════ 2c. Categories ═══════════ */}
      {categories.length > 0 && (
        <section data-testid="kids-public-categories" className="mt-8 px-3 sm:px-6 md:px-10 max-w-6xl mx-auto">
          <h2 className="font-display font-black text-xl sm:text-3xl text-white mb-3">Pick a vibe</h2>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 sm:gap-3">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                data-testid={`kids-cat-${c.slug}`}
                className={`aspect-square rounded-2xl bg-gradient-to-br ${c.color} flex flex-col items-center justify-center text-white font-black shadow-lg ring-2 ring-white/30 hover:scale-105 transition`}
              >
                <span className="text-2xl sm:text-3xl">{c.emoji}</span>
                <span className="text-[10px] sm:text-xs mt-1">{c.label}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ═══════════ 2d. Kids Movies ═══════════ */}
      {movies.length > 0 && (
        <section data-testid="kids-public-movies" className="mt-8 px-3 sm:px-6 md:px-10 max-w-6xl mx-auto">
          <h2 className="font-display font-black text-xl sm:text-3xl text-white mb-3 inline-flex items-center gap-2">
            <Film className="w-6 h-6 text-yellow-300" /> Kids Movies
          </h2>
          <div className="flex gap-3 overflow-x-auto pb-3 snap-x -mx-1 px-1" style={{ scrollbarWidth: "none" }}>
            {movies.map((m) => (
              <Link
                key={m.id}
                to={`/kids/watch/${m.id}`}
                data-testid={`kids-movie-tile-${m.id}`}
                className="snap-start shrink-0 w-36 sm:w-44"
              >
                <div className="aspect-[2/3] bg-black rounded-xl overflow-hidden ring-2 ring-white/15 hover:ring-yellow-300 transition">
                  {(m.poster_url || m.thumbnail_url)
                    ? <img src={m.poster_url || m.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full bg-gradient-to-br from-sky-500 to-indigo-700 flex items-center justify-center text-4xl">🎬</div>}
                </div>
                <div className="mt-1.5 text-white text-sm font-bold truncate">{m.title}</div>
                {m.duration_sec && <div className="text-white/50 text-[10px]">{Math.round(m.duration_sec / 60)} min</div>}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ═══════════ 3. Kid Creators Rail ═══════════ */}
      <section data-testid="kids-creators-rail" className="mt-10 px-3 sm:px-6 md:px-10 max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h2 className="font-display font-black text-xl sm:text-3xl text-white inline-flex items-center gap-2">
            <Users className="w-6 h-6 text-cyan-400" /> Meet the Kid Creators
          </h2>
          <div className="text-white/60 text-xs">{creators.length} profiles</div>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-3 snap-x -mx-1 px-1" style={{ scrollbarWidth: "none" }}>
          {creators.map((c) => (
            <Link
              key={c.user_id}
              to={`/kids/timeline/${c.user_id}`}
              data-testid={`kid-creator-${c.user_id}`}
              className="snap-start shrink-0 w-36 sm:w-44 bg-gradient-to-br from-white/10 to-white/5 border border-white/10 rounded-2xl p-3 text-center hover:ring-2 hover:ring-pink-400 transition"
            >
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-pink-400 via-fuchsia-500 to-amber-400 flex items-center justify-center text-4xl ring-4 ring-white/20">
                {c.picture
                  ? <img src={c.picture} alt="" className="w-full h-full object-cover rounded-full" />
                  : <span>{c.avatar_emoji || "⭐"}</span>}
              </div>
              <div className="mt-2 font-bold text-white text-sm truncate">{c.name}</div>
              <div className="text-white/50 text-[10px] truncate">@{c.handle}</div>
              {c.is_bot && <span className="inline-block mt-1 text-[9px] bg-fuchsia-500/30 text-fuchsia-200 font-bold px-2 py-0.5 rounded-full">BUDDY</span>}
              {!c.is_bot && <span className="inline-block mt-1 text-[9px] bg-emerald-500/30 text-emerald-200 font-bold px-2 py-0.5 rounded-full">KID CREATOR</span>}
            </Link>
          ))}
          {creators.length === 0 && (
            <div className="text-white/50 text-sm py-6">Loading creators…</div>
          )}
        </div>
      </section>

      {/* ═══════════ 4. Community feed ═══════════ */}
      <section data-testid="kids-public-feed" className="mt-10 px-3 sm:px-6 md:px-10 max-w-6xl mx-auto">
        <h2 className="font-display font-black text-xl sm:text-3xl text-white mb-4 inline-flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-yellow-300" /> What Kids Are Saying
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {feed.map((p) => (
            <div
              key={p.id}
              data-testid={`kids-feed-post-${p.id}`}
              className={`rounded-2xl p-4 bg-gradient-to-br ${p.color || "from-pink-400 to-fuchsia-500"} shadow-lg ring-2 ring-white/30 text-white relative overflow-hidden`}
            >
              <div className="text-[7vmin] min-text-[56px] leading-none drop-shadow">{p.emoji || "⭐"}</div>
              <div className="font-display font-black text-lg mt-1 leading-snug drop-shadow">{p.title}</div>
              <div className="mt-3 flex items-center gap-2 text-xs font-bold text-white/90">
                <span className="w-6 h-6 rounded-full bg-white/25 flex items-center justify-center text-sm">{p.creator_avatar || "⭐"}</span>
                @{p.handle || p.creator_name}
              </div>
            </div>
          ))}
          {feed.length === 0 && (
            <div className="text-white/50 text-sm col-span-full py-6 text-center">New posts coming in — check back in a few minutes!</div>
          )}
        </div>
      </section>

      {/* ═══════════ 5. Kid Mode + parent card ═══════════ */}
      <section data-testid="kids-kidmode-section" className="mt-10 px-3 sm:px-6 md:px-10 max-w-3xl mx-auto">
        <div className="bg-gradient-to-br from-yellow-500/10 to-pink-500/10 border border-yellow-300/25 rounded-2xl p-5 sm:p-6">
          <div className="flex items-center gap-2 text-yellow-200 text-xs uppercase tracking-[0.2em] font-black mb-2">
            <ShieldCheck className="w-4 h-4" /> Kid Mode
          </div>
          <div data-testid="kid-mode-card">
            {km.active ? (
              <div className="space-y-2" data-testid="kid-mode-active-card">
                <div className="text-white/80 text-sm">Kid Mode is ON — enter your PIN to unlock the full platform.</div>
                <div className="flex gap-2">
                  <input data-testid="kid-mode-exit-pin" type="password" inputMode="numeric" maxLength={8} value={exitPin}
                    onChange={(e) => setExitPin(e.target.value.replace(/\D/g, ""))}
                    placeholder="PIN"
                    className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm outline-none" />
                  <button data-testid="kid-mode-exit-btn" onClick={exit} className="bg-yellow-300 hover:bg-yellow-200 text-black font-bold px-4 py-2 rounded-lg text-sm inline-flex items-center gap-1">
                    <Unlock className="w-3.5 h-3.5" /> Exit
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2" data-testid="kid-mode-setup-card">
                <div className="text-white/80 text-sm">Set a 4-8 digit PIN. Only Kids content will show everywhere until you unlock.</div>
                <div className="flex gap-2">
                  <input data-testid="kid-mode-pin-input" type="password" inputMode="numeric" maxLength={8} value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                    placeholder="New PIN"
                    className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm outline-none" />
                  <button data-testid="kid-mode-enter-btn" onClick={enterKidMode} className="bg-yellow-300 hover:bg-yellow-200 text-black font-bold px-4 py-2 rounded-lg text-sm inline-flex items-center gap-1">
                    <Lock className="w-3.5 h-3.5" /> Lock
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Activity overlays */}
      {jrStudioOpen && <JrPodcastStudio onClose={() => setJrStudioOpen(false)} />}
      {drawOpen && <KidsDraw onClose={() => setDrawOpen(false)} />}
      {bedtimeOpen && <KidsBedtime onClose={() => setBedtimeOpen(false)} />}
      {shortsOpen && <KidsShorts onClose={() => setShortsOpen(false)} />}
    </div>
  );
}
